// tabPrefs — which destinations a person put on their own phone tab bar.
//
// The bar shipped as a fixed list per role (navigation.js `phone` + `phoneFor`).
// Those defaults are still what a new phone gets, and they are still chosen
// rather than arbitrary — but they cannot be right for everybody, because what
// a person taps twenty times a day is a fact about their job and not about
// their role. A crew lead who spends the morning on Approvals and a bookkeeper
// who lives in Estimates are both badly served by a list somebody else picked.
//
// So: the defaults are the starting point, and anyone can change them.
//
// ── Where this is stored, and why not on the server ─────────────────────────
//
// localStorage, per device. A server-side preference would need a column on
// `users`, and a column needs a MIGRATION — which CLAUDE.md rule 3 makes a
// human's decision that ships on its own, never bundled with feature work.
// This is the whole feature without one.
//
// The cost is real and worth stating plainly: the arrangement lives on the
// phone that made it. Sign in on a different handset and you get the defaults
// again. For a crew phone that is nearly always the same physical device that
// is a fair trade; if it stops being one, moving this to the server is a
// migration and a conversation, and this module's shape does not change.
//
// ── Everything here is pure ─────────────────────────────────────────────────
//
// The storage object is passed in rather than reached for, so every rule below
// is testable without a DOM and without mocking a global. `safeStorage()`
// wraps the real one for callers that just want it to work.

export const STORAGE_KEY = 'fw.tabs.v1';

/**
 * The most tabs a phone bar gets, NOT counting More.
 *
 * Four, down from five on 2026-09-05. Five plus More is six buttons across a
 * 390px screen — 65px each — and at that width `Crew board` and `Right now`
 * both wrapped onto two lines while `Report a problem` took three, which made
 * the bar half again as tall as it should be and the labels unreadable. Five
 * buttons at 78px is the widest arrangement that stays legible, so the limit
 * is what fits rather than what we would like to fit.
 */
export const TAB_LIMIT = 4;

/**
 * localStorage, or a stand-in that quietly does nothing.
 *
 * Private-mode Safari throws on `setItem` rather than failing softly, and a
 * browser set to block site data can throw on the ACCESSOR itself — so even
 * reaching for `window.localStorage` is wrapped. A phone that cannot remember
 * the arrangement must still show a working tab bar.
 */
export function safeStorage() {
  try {
    const s = typeof window !== 'undefined' ? window.localStorage : null;
    if (!s) return null;
    // Prove it actually works before handing it out; a quota-full or blocked
    // store answers here rather than at the first real write.
    const probe = `${STORAGE_KEY}.probe`;
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch (_) {
    return null;
  }
}

/**
 * The keys this person chose, or null when they have not chosen.
 *
 * null and [] are DIFFERENT and the difference is load-bearing: null means
 * "no preference, use the defaults", while [] means "I deliberately want no
 * tabs but More". Anything unreadable — absent, not JSON, not an array of
 * strings — reads as null, because a corrupt value must not be able to empty
 * somebody's navigation.
 */
export function readTabPrefs(storage) {
  if (!storage) return null;
  let raw;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  if (!parsed.every((k) => typeof k === 'string' && k)) return null;
  return parsed;
}

/** Save an arrangement. Returns whether it actually persisted. */
export function writeTabPrefs(storage, keys) {
  if (!storage || !Array.isArray(keys)) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(keys.filter((k) => typeof k === 'string' && k)));
    return true;
  } catch (_) {
    return false;
  }
}

/** Back to the defaults for this role. */
export function clearTabPrefs(storage) {
  if (!storage) return false;
  try {
    storage.removeItem(STORAGE_KEY);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * The bar, resolved: the person's arrangement filtered to what they can
 * actually reach, or the role's defaults when they have not arranged one.
 *
 * `reachable` is destinationsFor(role) and is the authority on both counts —
 * a saved key that is no longer in it is DROPPED rather than rendered. That
 * is not a rare case: a role change, or a destination this app stops offering
 * (Invoices left the bookkeeper's menu on 2026-09-05), both leave a stale key
 * behind in a store nothing else ever revisits. Rendering one would put a tab
 * on the bar that navigates to a screen the person is refused.
 *
 * Duplicates are collapsed and the list is capped, so no stored value can
 * produce a bar wider than the screen.
 *
 * @param reachable  destinationsFor(role) — every destination, in menu order
 * @param defaults   phoneTabsFor(role) — what a fresh phone gets
 * @param prefs      readTabPrefs(...) — null when unset
 */
export function resolveTabs(reachable, defaults, prefs, limit = TAB_LIMIT) {
  const byKey = new Map((reachable || []).map((d) => [d.key, d]));
  if (!Array.isArray(prefs)) return (defaults || []).slice(0, limit);
  const out = [];
  const seen = new Set();
  for (const key of prefs) {
    if (seen.has(key)) continue;
    const d = byKey.get(key);
    if (!d) continue;                 // not reachable for this role any more
    seen.add(key);
    out.push(d);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Everything NOT on the bar — the More sheet, in menu order.
 *
 * Derived from the resolved bar rather than from the defaults, so More always
 * holds exactly the complement of what is showing. Nothing a role can reach
 * can fall out of both.
 */
export function resolveMore(reachable, tabs) {
  const on = new Set((tabs || []).map((d) => d.key));
  return (reachable || []).filter((d) => !on.has(d.key));
}

/**
 * Move the item at `index` one place up (-1) or down (+1).
 *
 * Returns a NEW array, and the same array when the move would fall off either
 * end — so a caller can compare by identity to know whether anything happened
 * and a button at the end of the list is a no-op rather than a wrap-around.
 */
export function moveKey(keys, index, delta) {
  if (!Array.isArray(keys)) return keys;
  const to = index + delta;
  if (index < 0 || index >= keys.length || to < 0 || to >= keys.length) return keys;
  const next = keys.slice();
  const [moved] = next.splice(index, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Add or remove a key, honouring the cap.
 *
 * Adding past the limit returns the list unchanged rather than silently
 * dropping whichever tab happened to be last — the editor says the bar is
 * full instead, because a control that appears to work and quietly discards
 * the result is worse than one that explains itself.
 */
export function toggleKey(keys, key, limit = TAB_LIMIT) {
  const list = Array.isArray(keys) ? keys : [];
  if (list.includes(key)) return list.filter((k) => k !== key);
  if (list.length >= limit) return list;
  return [...list, key];
}
